import {
  ArrowLeft,
  Atom,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Lightbulb,
  Pause,
  Play,
  RotateCcw,
  Sparkles,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import {
  DENSITY_SUBSTANCES,
  HEATING_PHASE_ENERGY_NOTES,
  HEATING_PHASE_LABELS,
  PARTICLE_MODEL_MODES,
  PARTICLE_MODEL_SOURCES,
  STATE_PROPERTIES,
  WATER_PHASE_NAMES,
  casesForMode,
  checkParticleCase,
  formatNumber,
  speedFactor,
  type HeatingPhase,
  type MatterState,
  type ParticleMode,
} from './particleModel';
import { ParticleExploreTabs } from './ParticleExploreTabs';
import { HeatingSetupPanel, HeatingWorkingPanel } from './ParticleHeatingControls';
import { CaseVisualPanel, HeatingCurveChart, ParticleBox, WaterMolecule } from './ParticleModelScenes';
import { StudyShell } from './StudyShell';
import { WaterBeaker3D } from './WaterBeaker3D';
import { useAnimationTime } from './useAnimationTime';
import { modeForYear, useChemistryToolYear } from './useChemistryToolYear';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { BEAKER_CAPACITY_G, DEFAULT_HEATING_SETUP, HEATING_CHALLENGES, HEATING_MODEL_MAX_C, HEATING_QUESTION, calculateHeating, timeToReach } from './waterHeatingCalc';
import { ExploreChallenges } from './ExploreChallenges';

const STATES: MatterState[] = ['solid', 'liquid', 'gas'];

const MODE_COPY: Record<ParticleMode, { eyebrow: string; title: string; description: string }> = {
  states: {
    eyebrow: 'Year 7 · States of matter',
    title: 'Predict what particles do',
    description: 'Use the particle model to predict changes of state, spreading, squashing and density.',
  },
  explain: {
    eyebrow: 'Year 8 · Explain with particles',
    title: 'Explain why it happens',
    description: 'Choose the explanation that matches the particle model, then check it against the model data.',
  },
};

/** Jump to a stage: the page works out the heating time that gets this setup there. */
const HEATING_PRESETS: Array<{ id: string; label: string; phase: HeatingPhase; fraction: number }> = [
  { id: 'ice', label: 'Ice warming', phase: 'solid', fraction: 0.5 },
  { id: 'melting', label: 'Half melted', phase: 'melting', fraction: 0.5 },
  { id: 'water', label: 'Water warming', phase: 'liquid', fraction: 0.5 },
  { id: 'boiling', label: 'Half boiled away', phase: 'boiling', fraction: 0.5 },
  { id: 'steam', label: 'Steam heating', phase: 'gas', fraction: 0.5 },
];

function presentStates(fractions: Record<MatterState, number>): MatterState[] {
  return STATES.filter((state) => fractions[state] > 0);
}

export function ParticleModelLabPage() {
  const year = useChemistryToolYear();
  const [manualMode, setManualMode] = useState<ParticleMode | null>(null);
  const mode = manualMode ?? modeForYear(PARTICLE_MODEL_MODES, year);
  const cases = useMemo(() => casesForMode(mode), [mode]);
  const [caseIndex, setCaseIndex] = useState(0);
  const [selected, setSelected] = useState<string | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [setup, setSetup] = useState(DEFAULT_HEATING_SETUP);
  const reducedMotion = usePrefersReducedMotion();
  const [playing, setPlaying] = useState<boolean | null>(null);
  const moving = playing ?? !reducedMotion;
  const time = useAnimationTime(moving);

  const current = cases[Math.min(caseIndex, cases.length - 1)];
  const result = revealed && selected ? checkParticleCase(current, selected) : null;
  const calculation = useMemo(() => calculateHeating(setup), [setup]);
  const heating = calculation.state;
  const attractionsJ = calculation.steps
    .filter((step) => step.phase === 'melting' || step.phase === 'boiling')
    .reduce((sum, step) => sum + step.usedJ, 0);

  const resetCase = (index: number) => {
    setCaseIndex(index);
    setSelected(null);
    setRevealed(false);
  };

  const chooseMode = (next: ParticleMode) => {
    setManualMode(next);
    resetCase(0);
  };

  return (
    <StudyShell scienceTutor={{ subjectKey: 'chemistry', toolId: 'particle-model', state: { mode, setup, heating_calculation: calculation, case_id: current.id, answer_revealed: revealed, ...(revealed ? { visible_result: result } : {}) } }}>
      <Link
        to="/learn/study/subjects/chemistry/tools"
        className="inline-flex items-center gap-2 text-sm font-extrabold text-slate2 hover:text-ink"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden="true" /> Chemistry tools
      </Link>

      <header className="chemistry-tool-header mt-5 rounded-[2rem] border border-brand-sky/25 bg-gradient-to-br from-wash-sky via-canvas-pure to-wash-mint p-6 shadow-card-soft lg:p-8">
        <div className="flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div className="max-w-3xl">
            <p className="flex items-center gap-2 text-xs font-extrabold uppercase tracking-[0.16em] text-brand-sky">
              <Atom className="h-4 w-4" aria-hidden="true" /> Matter &amp; change
            </p>
            <h1 className="mt-3 text-3xl font-extrabold tracking-tight text-ink sm:text-4xl">
              Particle Model &amp; States Lab
            </h1>
            <p className="mt-3 max-w-2xl font-medium leading-7 text-slate2">
              Heat and cool water, watch the particles, then use the same model to predict
              spreading, squashing and density.
            </p>
          </div>
          <div className="rounded-2xl border border-white/80 bg-white/80 px-4 py-3 text-sm font-bold text-ink-soft">
            Explore tool · Year 7–8
          </div>
        </div>
        <div className="mt-6 grid max-w-xl grid-cols-2 rounded-full bg-white/75 p-1" aria-label="Choose learning level">
          {PARTICLE_MODEL_MODES.map((option) => (
            <button
              key={option.mode}
              type="button"
              aria-pressed={mode === option.mode}
              onClick={() => chooseMode(option.mode)}
              className={`rounded-full px-3 py-2.5 text-sm font-extrabold transition-colors ${
                mode === option.mode ? 'bg-ink text-white shadow-card-soft' : 'text-slate2 hover:bg-white'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </header>

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8" aria-labelledby="explorer-heading">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Heat and cool</p>
            <h2 id="explorer-heading" className="mt-2 text-2xl font-extrabold text-ink">
              Heat your own sample of water
            </h2>
            <p className="mt-1 text-sm font-medium text-slate2">
              Choose the mass, starting temperature, heater power and time. Pure water at 101.325 kPa (sea-level pressure); the
              model stops at {HEATING_MODEL_MAX_C} °C and ignores heat lost to the room.
            </p>
          </div>
          <button
            type="button"
            onClick={() => setPlaying(!moving)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline px-4 py-2 text-sm font-extrabold text-ink"
          >
            {moving ? <Pause className="h-4 w-4" aria-hidden="true" /> : <Play className="h-4 w-4" aria-hidden="true" />}
            {moving ? 'Pause particles' : 'Play particles'}
          </button>
        </div>

        {heating ? (
          <div className="mt-5">
            <p className="text-2xl font-extrabold text-ink" data-testid="water-phase-name">
              {WATER_PHASE_NAMES[heating.phase]}
              <span className="ml-2 text-xl font-extrabold text-brand-coral">{formatNumber(heating.temperatureC)} °C</span>
            </p>
            <div className="mt-3 grid gap-4 md:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <figure className="min-w-0">
                <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">What you see</figcaption>
                <WaterBeaker3D
                  state={{ phase: heating.phase, fractions: heating.fractions, temperatureC: heating.temperatureC, fill: setup.massG / BEAKER_CAPACITY_G }}
                  playing={moving}
                  label={`Beaker of ${formatNumber(setup.massG, 0)} g of water at ${formatNumber(heating.temperatureC)} °C: ${WATER_PHASE_NAMES[heating.phase]}`}
                />
              </figure>
              <figure className="min-w-0">
                <figcaption className="mb-2 text-xs font-extrabold uppercase tracking-[0.12em] text-slate2">Zoomed in: water molecules</figcaption>
                <ParticleBox
                  fractions={heating.fractions}
                  temperatureC={heating.temperatureC}
                  time={time}
                  molecule="water"
                  label={`Water molecules at ${formatNumber(heating.temperatureC)} °C: ${WATER_PHASE_NAMES[heating.phase]}, ${HEATING_PHASE_LABELS[heating.phase].toLowerCase()}`}
                />
                <p className="mt-2 flex items-start gap-2 text-xs font-medium text-slate2">
                  <svg viewBox="-4 -3 8 6.5" className="h-5 w-6 shrink-0" aria-hidden="true">
                    <WaterMolecule x={0} y={-0.4} angle={0} />
                  </svg>
                  <span>
                    = one water molecule, H₂O: red oxygen (O) and two white hydrogens (H). The same molecules in every state; only
                    arrangement, spacing and speed change. Not to scale.
                  </span>
                </p>
              </figure>
            </div>
          </div>
        ) : null}

        <div className="mt-5 grid gap-5 lg:grid-cols-2">
          <div className="min-w-0">
            <div>
              <HeatingSetupPanel setup={setup} onChange={setSetup} />
            </div>
            <div className="mt-3 flex flex-wrap gap-2" aria-label="Heating presets">
              {HEATING_PRESETS.map((preset) => {
                const timeS = timeToReach(setup, preset.phase, preset.fraction);
                return (
                  <button
                    key={preset.id}
                    type="button"
                    disabled={timeS === null}
                    onClick={() => timeS !== null && setSetup({ ...setup, timeS })}
                    className="rounded-full border border-hairline bg-surface px-3 py-1.5 text-xs font-extrabold text-ink hover:border-brand-sky/50 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    {preset.label}
                  </button>
                );
              })}
            </div>
            <div className="mt-4">
              <ExploreChallenges question={HEATING_QUESTION} challenges={HEATING_CHALLENGES} result={{ setup, result: calculation }} />
            </div>
            <div className="mt-4">
              <HeatingCurveChart segments={calculation.segments} energyJ={heating.energyJ} temperatureC={heating.temperatureC} massG={setup.massG} />
            </div>
          </div>
          <div className="min-w-0">
            <HeatingWorkingPanel result={calculation} massG={setup.massG} />
          </div>
        </div>

        {heating ? (
          <div className="mt-5 grid gap-3 md:grid-cols-3" role="group" aria-label="Model readout">
            <div className="rounded-2xl border border-brand-coral/25 bg-wash-coral p-4">
              <p className="text-xs font-extrabold uppercase tracking-wide text-brand-coral">Temperature</p>
              <p className="mt-1 text-2xl font-extrabold text-ink">{formatNumber(heating.temperatureC)} °C</p>
              <p className="mt-1 text-xs font-bold text-slate2">{HEATING_PHASE_LABELS[heating.phase]}</p>
            </div>
            <div className="rounded-2xl border border-brand-sky/25 bg-wash-sky p-4 md:col-span-2">
              <p className="text-xs font-extrabold uppercase tracking-wide text-brand-sky">Where the energy goes</p>
              <p className="mt-1 text-sm font-bold leading-6 text-ink">{HEATING_PHASE_ENERGY_NOTES[heating.phase]}</p>
              {mode === 'explain' ? (
                <dl className="mt-3 grid gap-2 text-xs font-bold text-slate2 sm:grid-cols-2">
                  <div>
                    <dt>Energy used to overcome attractions</dt>
                    <dd className="text-base font-extrabold text-ink">{Math.round(attractionsJ)} J</dd>
                  </div>
                  <div>
                    <dt>Average particle speed (relative to 20 °C)</dt>
                    <dd className="text-base font-extrabold text-ink">{formatNumber(speedFactor(heating.temperatureC), 2)}×</dd>
                  </div>
                </dl>
              ) : null}
            </div>
          </div>
        ) : null}

        {heating ? (
          <div className="mt-4 grid gap-3 md:grid-cols-3">
            {presentStates(heating.fractions).map((state) => (
              <div key={state} className="rounded-2xl border border-hairline bg-surface p-4 text-xs font-medium leading-5 text-slate2">
                <p className="text-sm font-extrabold text-ink">
                  {STATE_PROPERTIES[state].label}
                  {heating.plateau ? ` · ${Math.round(heating.fractions[state] * 100)}% of particles` : ''}
                </p>
                <p className="mt-1"><strong className="text-ink-soft">Arrangement:</strong> {STATE_PROPERTIES[state].arrangement}</p>
                <p><strong className="text-ink-soft">Motion:</strong> {STATE_PROPERTIES[state].motion}</p>
                <p><strong className="text-ink-soft">Attraction:</strong> {STATE_PROPERTIES[state].attraction}</p>
                <p><strong className="text-ink-soft">Compressing:</strong> {STATE_PROPERTIES[state].compressibility}</p>
              </div>
            ))}
          </div>
        ) : null}
      </section>

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8" aria-labelledby="explore-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-coral">Explore</p>
        <h2 id="explore-heading" className="mt-2 text-2xl font-extrabold text-ink">
          Try your own numbers
        </h2>
        <p className="mt-1 max-w-2xl text-sm font-medium leading-6 text-slate2">
          Type a mass and volume, squeeze a syringe or change the temperature. The working, the picture and the result all
          come from the same model.
        </p>
        <div className="mt-5">
          <ParticleExploreTabs time={time} />
        </div>
      </section>

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6 lg:p-8" aria-labelledby="predict-heading">
        <p className="text-xs font-extrabold uppercase tracking-[0.14em] text-brand-sky">Practice · {MODE_COPY[mode].eyebrow}</p>
        <h2 id="predict-heading" className="mt-2 text-2xl font-extrabold text-ink">{MODE_COPY[mode].title}</h2>
        <p className="mt-1 max-w-2xl text-sm font-medium leading-6 text-slate2">{MODE_COPY[mode].description}</p>

        <div className="mt-5 flex flex-wrap items-center gap-2">
          <button
            type="button"
            aria-label="Previous case"
            disabled={caseIndex === 0}
            onClick={() => resetCase(caseIndex - 1)}
            className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40"
          >
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          </button>
          <div className="min-w-0 flex-1">
            <select
              aria-label="Choose a case"
              value={current.id}
              onChange={(event) => resetCase(cases.findIndex((item) => item.id === event.target.value))}
              className="w-full rounded-2xl border border-hairline bg-surface px-3 py-2 text-sm font-extrabold text-ink"
            >
              {cases.map((item, index) => (
                <option key={item.id} value={item.id}>
                  {index + 1}. {item.title}
                </option>
              ))}
            </select>
          </div>
          <button
            type="button"
            aria-label="Next case"
            disabled={caseIndex >= cases.length - 1}
            onClick={() => resetCase(caseIndex + 1)}
            className="rounded-full border border-hairline p-2 text-ink disabled:opacity-40"
          >
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        <h3 className="mt-5 text-lg font-extrabold text-ink">{current.title}</h3>
        <p className="mt-1 max-w-3xl font-medium leading-7 text-slate2">{current.prompt}</p>
        <div className="mt-4 grid gap-2 sm:grid-cols-2" aria-label="Your prediction">
          {current.options.map((option) => (
            <button
              key={option.id}
              type="button"
              disabled={revealed}
              aria-pressed={selected === option.id}
              onClick={() => setSelected(option.id)}
              className={`rounded-2xl border px-4 py-3 text-left text-sm font-extrabold transition-colors disabled:cursor-default ${
                selected === option.id ? 'border-brand-sky bg-wash-sky text-ink' : 'border-hairline bg-surface text-slate2 hover:border-brand-sky/40'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
        <div className="mt-4 flex flex-wrap gap-3">
          <button
            type="button"
            disabled={!selected || revealed}
            onClick={() => setRevealed(true)}
            className="inline-flex items-center gap-2 rounded-full bg-ink px-5 py-3 text-sm font-extrabold text-white disabled:cursor-not-allowed disabled:opacity-40"
          >
            <Sparkles className="h-4 w-4" aria-hidden="true" /> Check with the model
          </button>
          <button
            type="button"
            onClick={() => resetCase(caseIndex)}
            className="inline-flex items-center gap-2 rounded-full border border-hairline px-5 py-3 text-sm font-extrabold text-ink hover:bg-surface"
          >
            <RotateCcw className="h-4 w-4" aria-hidden="true" /> Try again
          </button>
        </div>

        {result ? (
          <div
            role="status"
            className={`mt-5 rounded-[1.5rem] border p-5 ${result.correct ? 'border-brand-mint/40 bg-wash-mint' : 'border-brand-coral/35 bg-wash-coral'}`}
          >
            <div className="flex items-start gap-3">
              {result.correct ? (
                <CheckCircle2 className="mt-0.5 h-6 w-6 shrink-0 text-brand-mint" aria-hidden="true" />
              ) : (
                <Lightbulb className="mt-0.5 h-6 w-6 shrink-0 text-brand-coral" aria-hidden="true" />
              )}
              <div>
                <p className="font-extrabold text-ink">
                  {result.correct ? `Matches the model — ${result.expected.label}` : `Not quite — the model shows: ${result.expected.label}`}
                </p>
                <p className="mt-2 font-medium leading-7 text-slate2">{result.chosen.feedback}</p>
                <p className="mt-2 text-sm font-bold leading-6 text-ink-soft">{result.explanation}</p>
              </div>
            </div>
          </div>
        ) : null}

        <div className="mt-5">
          {revealed ? (
            <CaseVisualPanel visual={current.visual} time={time} />
          ) : (
            <p className="rounded-2xl border border-dashed border-hairline bg-surface px-4 py-6 text-center text-sm font-bold text-slate2">
              Predict first. The model view for this case appears after you check.
            </p>
          )}
        </div>
      </section>

      <section className="mt-6 rounded-[2rem] border border-hairline bg-canvas-pure p-5 shadow-card-soft sm:p-6" aria-labelledby="data-heading">
        <h2 id="data-heading" className="text-lg font-extrabold text-ink">Reference data and conditions</h2>
        <div className="mt-3 overflow-x-auto">
          <table className="w-full min-w-[22rem] text-left text-xs">
            <caption className="sr-only">Density of reviewed substances</caption>
            <thead>
              <tr className="text-slate2">
                <th className="py-1 pr-3 font-extrabold">Substance</th>
                <th className="py-1 pr-3 font-extrabold">Density (g/cm³)</th>
                <th className="py-1 font-extrabold">Conditions</th>
              </tr>
            </thead>
            <tbody>
              {DENSITY_SUBSTANCES.map((item) => (
                <tr key={item.id} className="border-t border-hairline">
                  <td className="py-1 pr-3 font-bold text-ink">{item.name}</td>
                  <td className="py-1 pr-3 font-bold text-ink">{item.densityGPerCm3}</td>
                  <td className="py-1 font-medium text-slate2">{item.conditions}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <details className="mt-4 text-xs font-medium leading-5 text-slate2">
          <summary className="cursor-pointer font-extrabold text-ink">Sources</summary>
          <ul className="mt-2 list-disc space-y-1 pl-5">
            {PARTICLE_MODEL_SOURCES.map((source) => (
              <li key={source.id}>{source.citation}</li>
            ))}
          </ul>
          <p className="mt-2">Values are awaiting teaching review.</p>
        </details>
      </section>

      <footer className="mt-6 rounded-[1.5rem] border border-hairline bg-surface px-5 py-4 text-sm font-medium leading-6 text-slate2">
        Model boundary: particle pictures are simplified and not to scale; real particles are far
        smaller and more numerous. The heating curve assumes steady heating of pure water with no
        energy loss. Diffusion rates are typical orders of magnitude, not measurements. Nothing on
        this page is saved or scored.
      </footer>
    </StudyShell>
  );
}
